import { AfterViewInit, Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { timeStamp } from 'console';
import { DatabaseOverview } from 'reg-hub-common';

@Component({
    selector: 'app-database-overview',
    templateUrl: './database-overview.component.html',
    styleUrls: ['./database-overview.component.css'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DatabaseOverviewComponent implements AfterViewInit, OnChanges {

  @ViewChild(MatSort) sort!: MatSort;
  
  @Input() dataSource!: MatTableDataSource<DatabaseOverview, MatPaginator>;
  @Input() loading: boolean = false;

  protected displayedColumns = [   
    'databaseName',
    'numPendings'
   ];
  
  constructor(private router: Router) { }

  ngOnChanges(changes: SimpleChanges): void {
    // dataSource is populated asynchronously by the dashboard's forkJoin, so it is
    // undefined on first bind. Recompute on every change rather than once in ngOnInit,
    // which used to be safe only because the dashboard withheld this component until
    // its data had loaded.
    if (changes['dataSource']) {
      this.updateDisplayedColumns();
      this.applySort();
    }
  }

  ngAfterViewInit(): void {
    this.applySort();
  }

  private applySort(): void {
    if (this.dataSource && this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  private updateDisplayedColumns(): void {
    //Set Column Visibility - Pendings always
    const data = this.dataSource?.data ?? [];
    const columns = ['databaseName', 'numPendings'];

    if (data.some(d => d.numInvalids > 0)) {
      columns.push('numInvalids');
    }

    if (data.some(d => d.numStaged > 0)) {
      columns.push('numStaged');
    }

    if (data.some(d => d.numHighVolume > 0)) {
      columns.push('numHighVolume');
    }

    if (data.some(d => d.numRequestedAtRegistry > 0)) {
      columns.push('numRequestedAtRegistry');
    }

    if (data.some(d => d.numOutOfRegistryHours > 0)) {
      columns.push('numOutOfRegistryHours');
    }

    if (data.some(d => d.numRegistryOutage > 0)) {
      columns.push('numRegistryOutage');
    }

    if (data.some(d => d.numAwaitingExternalResults > 0)) {
      columns.push('numAwaitingExternalResults');
    }

    this.displayedColumns = columns;
  }

  protected getButtonColor(numOrders: number) {
    let value = 255 - numOrders * 5;

    if(value >= 205 && numOrders !== 0) {
      value = 205;
    } else if(value < 0) {
      value = 0;
    }

    return { 'background-color': `rgb(255, ${value}, ${value})` };
  }

  protected getPendingColour(oldest: Date | null) {
    if (!oldest) {
      return { 'background-color': `rgb(255, 255, 255)` };
    }
    else {
      let currentDate = new Date();
      let oldestDate = new Date(oldest);
      let timeSpan = Math.abs(currentDate.getTime() - oldestDate.getTime());
      if (timeSpan > (1000 * 60 * 5)) {
        //Show Red if older that 5 minutes
        return { 'background-color': `rgb(255, 100, 100)` };
      }
      else
      {
        return { 'background-color': `rgb(255, 255, 255)` };
      }
    }
  }

  protected onViewPendings(overview: DatabaseOverview) {
    const params = {
      status: 'Pending',
      databaseID: overview.databaseID,
      isVisible: true,
      sortColumn: 'added',
      sortDirection: 'asc'
    }

    this.router.navigate(['/orders'], { queryParams: params });
  }

  protected getHighVolumeColour(oldest: Date | null) {
    if (!oldest) {
      return { 'background-color': `rgb(255, 255, 255)` };
    }
    else {
      let startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      let oldestDate = new Date(oldest);
      if (oldestDate < startOfToday) {
        //Show Red if waiting since a previous day - ON High Volume runs daily,
        //so anything held over means the run hasn't happened or didn't clear it
        return { 'background-color': `rgb(255, 100, 100)` };
      }
      else
      {
        return { 'background-color': `rgb(255, 255, 255)` };
      }
    }
  }

  protected onViewHighVolume(overview: DatabaseOverview) {
    const params = {
      status: 'AwaitingHighVolumeResults',
      databaseID: overview.databaseID,
      isVisible: true,
      sortColumn: 'priority',
      sortDirection: 'asc'
    }

    this.router.navigate(['/orders'], { queryParams: params });
  }

  protected getRequestedAtRegistryColour(oldest: Date | null) {
    if (!oldest) {
      return { 'background-color': `rgb(255, 255, 255)` };
    }
    else {
      let currentDate = new Date();
      let oldestDate = new Date(oldest);
      let timeSpan = Math.abs(currentDate.getTime() - oldestDate.getTime());
      if (timeSpan > (1000 * 60 * 60 * 24 * 7)) {
        //Show Red if older than 7 days
        return { 'background-color': `rgb(255, 100, 100)` };
      }
      else
      {
        return { 'background-color': `rgb(255, 255, 255)` };
      }
    }
  }

  protected onViewRequestedAtRegistry(overview: DatabaseOverview) {
    const params = {
      status: 'RequestedAtRegistry',
      databaseID: overview.databaseID,
      isVisible: true,
      sortColumn: 'priority',
      sortDirection: 'asc'
    }

    this.router.navigate(['/orders'], { queryParams: params });
  }

  protected onViewOutOfHours(overview: DatabaseOverview) {
    const params = {
      status: 'OutOfRegistryHours',
      databaseID: overview.databaseID,
      isVisible: true,
      sortColumn: 'priority',
      sortDirection: 'asc'
    }

    this.router.navigate(['/orders'], { queryParams: params });
  }

  protected getInvalidColour(oldest: Date | null) {
    if (!oldest) {
      return { 'background-color': `rgb(255, 255, 255)` };
    }
    else {
      let currentDate = new Date();
      let oldestDate = new Date(oldest);
      let timeSpan = Math.abs(currentDate.getTime() - oldestDate.getTime());
      if (timeSpan > (1000 * 60 * 60 * 24 * 30)) {
        //Show Red if older that 30 Days
        return { 'background-color': `rgb(255, 100, 100)` };
      }
      else
      {
        return { 'background-color': `rgb(255, 255, 255)` };
      }
    }
  }

  protected onViewInvalids(overview: DatabaseOverview) {
    const params = {
      status: 'Invalid',
      databaseID: overview.databaseID,
      isVisible: true,
      sortColumn: 'added',
      sortDirection: 'asc'
    }

    this.router.navigate(['/orders'], { queryParams: params });
  }

  protected onViewStaged(overview: DatabaseOverview) {
    const params = {
      status: 'Staged',
      databaseID: overview.databaseID,
      isVisible: true,
      sortColumn: 'added',
      sortDirection: 'asc'
    }

    this.router.navigate(['/orders'], { queryParams: params });
  }

  protected onViewRegistryOutage(overview: DatabaseOverview) {
    const params = {
      status: 'RegistryOutage',
      databaseID: overview.databaseID,
      isVisible: true,
      sortColumn: 'added',
      sortDirection: 'asc'
    }

    this.router.navigate(['/orders'], { queryParams: params });
  }

  protected onViewAwaitinExternal(overview: DatabaseOverview) {
    const params = {
      status: 'AwaitingExternalInformation',
      databaseID: overview.databaseID,
      isVisible: true,
      sortColumn: 'added',
      sortDirection: 'asc'
    }

    this.router.navigate(['/orders'], { queryParams: params });
  }
}
